/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Checkbox,
  List,
  ListItem,
  Typography,
  Popover,
  Divider
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import ProTypeModal from "./component/selectTypeModal/index";
import { DEF_ADDRESS_BODY } from "@/common-components-src/api/address/address.interface";
import to from "await-to-js";
import { selectAll } from "@/app/csr-src/apis/cart/cart.api";
import VectorDown from "@/app/csr-src/assets/icons/VectorDown.svg";
import ItemDelete from "./icons/ItemDelete.svg";
import { getUserUUIDFromCookie } from "@/utils/userUtils";
import { useTranslation } from "@/i18n/client";
import {
  CartData,
  SubtotalInfo,
  BodyParam,
  ProductItemInfo,
  PRODUCT_ITEM_CORE_KEY, PRODUCT_ITEM_BASIC_KEY, PRODUCT_ITEM_EXTINFO_KEY
} from "@/app/csr-src/apis/cart/cart.interface";
import JDIPrice from "@/common-components-src/js/price/index";
import { imageUtil } from "@/common-components-src/js/utils/imageUtil";
import UKCounter from "@/common-components-src/components/UKCounter";
import UKToast from "@/common-components-src/components/UKToast/index";
import UKImg from "@/common-components-src/components/UKImg";
import Countdown from "@/common-components-src/js/countdown/index";
import styles from "./cartList.module.scss";
import { gotoDp } from "@/app/csr-src/utils/goto";
import {
  safeParse,
  handleDeleteParams,
  getProductItems,
  updateProductItems,
  updateProductItemParams,
  handleChangeNumParams,
  handleItemCheckParams,
  handleSelectDeleteParams,
} from "./utils/cartUtils";
import { sendClickTrack, EXPOSURE_RECORD } from "@/tracks/25226";
import { counterTrack, productCardTrack } from "./utils/cartTrackUtils";
import { eventTrackingUtils } from "@/utils/eventTrackingUtils";

interface CartItemProps {
  cartData: CartData | null;
  loadDataCheck?: (bodyParam: BodyParam) => void;
  setCartData?: (cart: CartData | null) => void;
  tabValue?: string;
  refreshList?: () => void;
}

const CartList: React.FC<CartItemProps> = observer(
  ({ cartData, loadDataCheck, setCartData, tabValue, refreshList }) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false); // 展开商品类型标签
    const [productItems, setProductItems] = useState<{ info: ProductItemInfo }[] | null>(null); // 商品列表
    const [productItemsNoStock, setProductItemsNoStock] = useState<{ info: ProductItemInfo }[] | null>(null); // 无货商品列表
    const [productItemsRemove, setProductItemsRemove] = useState<{ info: ProductItemInfo }[] | null>(null); // 下架商品列表
    const [subtotal, setSubtotal] = useState<{ info: SubtotalInfo } | null>(null); // 商品总量信息
    const [remainingTimesMap, setRemainingTimesMap] = useState<Record<string, number>>({}); // 倒计时
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null); // 选择商品类型弹窗
    const [isOpenTypeSelect, setIsOpenTypeSelect] = useState(false);
    const id = isOpenTypeSelect ? "simple-popover" : undefined;
    const [isSelectAllDelete, setIsSelectAllDelete] = useState(false); // 是否选择全部删除
    const [curProItemType, setCurProItemType] = useState<any | null>(null); // 当前的商品类型
    const [curSkuId, setSkuId] = useState(""); // 当前SKuID
    // 添加一个 ref 来追踪是否正在刷新
    const isRefreshing = useRef(false);

    const handleClose = () => {
      setIsOpenTypeSelect(false);
      setAnchorEl(null);
      setIsExpanded(false);
      setCurProItemType(null);
    };

    useEffect(() => {
      if (!cartData || !cartData.data) {
        return;
      }
      // 商品列表楼层
      const { productItemsHasStock, productItemsNoStock, productItemsReMove } = getProductItems(cartData);
      setProductItems(productItemsHasStock || []);
      setProductItemsNoStock(productItemsNoStock || []);
      setProductItemsRemove(productItemsReMove || []);
      // 商品总量信息楼层
      const subTotals = Object.values(cartData.data).filter((item) => {
        if (
          "type" in item &&
          item.type &&
          item.type.namespace === "core.trade-DM#cartView.subtotal-M#defaultSubtotalItem"
        ) {
          return true;
        }
        return false;
      }) as Array<{ info: SubtotalInfo }>;
      if (subTotals && subTotals.length > 0) {
        setSubtotal(subTotals["0"]);
      }
    }, [cartData]);

    const handleCheckAll = (isChecked: boolean) => {
      const requestData: BodyParam = {
        verticalTag: "cn_ybxt_b2c",
        area: DEF_ADDRESS_BODY.area,
        cartuuid: getUserUUIDFromCookie(),
        userActionId: isChecked ? "selectAll" : "unselectAll",
        extendParamMap: {
          stock: true,
        },
      };
      if (loadDataCheck) {
        loadDataCheck(requestData);
      }
    };

    const handleItemCheck = (item: any) => {
      const requestData = handleItemCheckParams(item);
      if (loadDataCheck) {
        loadDataCheck(requestData);
      }
    };

    const handleChangeNum = (item: any, num: number) => {
      const requestData = handleChangeNumParams(item, num);
      if (loadDataCheck) {
        loadDataCheck(requestData);
      }
    };

    const updateItemData = (targetSkuId: string) => {
      const requestData = updateProductItemParams(targetSkuId, curProItemType);
      otherLoadCartDetails(requestData);
    };

    const handleExpand = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setIsExpanded(!isExpanded);
      setIsOpenTypeSelect(true);
    };

    useEffect(() => {
      // 重置刷新状态
      isRefreshing.current = false;
      [...productItems || [], ...productItemsNoStock || []]?.forEach((item) => {
        // 未下架商品才执行
        if (item.info[PRODUCT_ITEM_EXTINFO_KEY]) {
          // 提取 productMarkInfo 的值
          const productMarkInfoValues = item?.info[PRODUCT_ITEM_EXTINFO_KEY]?.productMarkInfo;
          const productMarkSet = item?.info[PRODUCT_ITEM_EXTINFO_KEY]?.productMarkSet || [];
          // 根据 productMarkSet 的顺序获取对应的标记信息
          const sortedMarkInfos = productMarkSet?.map((markId) => {
            const markInfoJson = productMarkInfoValues && productMarkInfoValues[markId];
            return safeParse(markInfoJson);
          });
          sortedMarkInfos
            .filter((markInfo) => markInfo?.type === 1)
            .forEach((markInfo, index) => {
              const endTime = markInfo?.endTime;
              if (endTime) {
                const key = `timer-${item?.info[PRODUCT_ITEM_CORE_KEY]?.skuId}`;
                if (!remainingTimesMap[key]) {
                  setRemainingTimesMap((prevMap) => ({
                    ...prevMap,
                    [key]: endTime,
                  }));
                }
              }
            });
        }
      });
      return () => {
        isRefreshing.current = false;
      };
    }, [productItems, productItemsNoStock]);

    const handleCountdownEnd = (endTime: number, key: string) => {
      setRemainingTimesMap((prevMap) => {
        // 只有当remainingTimesMap中还有这个key的时候才刷新
        if (key in prevMap && !isRefreshing.current) {
          isRefreshing.current = true;
          const newMap = { ...prevMap };
          delete newMap[key]; // 移除已结束的倒计时
          refreshList && refreshList();
          return newMap;
        }
        return prevMap;
      });
    };

    const markInfoFloor = (parsedMarkInfos: any, item: any) => {
      return (
        <div className={styles["markInfoFloorParent"]}>
          {parsedMarkInfos.length > 0 &&
            parsedMarkInfos
              .filter((markInfo: any) => markInfo?.type === 1)
              .map((markInfo: any, index: number) => {
                const key = `timer-${item?.info[PRODUCT_ITEM_CORE_KEY]?.skuId}`;
                const endTime = markInfo?.endTime;
                return (
                  <div
                    key={index}
                    className={styles["tab-tag"]}
                    style={{
                      background: markInfo?.bgColor ? markInfo.bgColor : "",
                    }}
                  >
                    {markInfo?.icon && (
                      <img style={{ width: "12px", height: "12px" }} src={markInfo.icon} />
                    )}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: markInfo?.richText,
                      }}
                    ></span>
                    {endTime && remainingTimesMap[key] > 0 && (
                      <span className={styles["flash"]} style={{ color: markInfo?.textColor }}>
                        <Countdown
                          countdown={endTime}
                          format={markInfo?.format}
                          onEnd={() => handleCountdownEnd(endTime, key)}
                        />
                      </span>
                    )}
                  </div>
                );
              })}
        </div>
      );
    };

    const handleAgainClose = () => {
      setIsSelectAllDelete(false);
    };

    const handleDeleteAll = () => {
      const requestData = handleSelectDeleteParams(cartData);
      otherLoadCartDetails(requestData);
    };

    const handleDeleteSingle = (item: any) => {
      // 删除选中的多个
      if (isSelectAllDelete) {
        handleDeleteAll();
        return;
      }
      const requestData = handleDeleteParams(item);
      otherLoadCartDetails(requestData);
    };

    const otherLoadCartDetails = async (bodyParam: BodyParam) => {
      const [err, result] = await to(selectAll(bodyParam));

      if (err) {
        if (setCartData) {
          setCartData(null);
        }
      } else {
        if (setCartData) {
          setCartData(result as CartData);
        }
        handleAgainClose();
      }
    };

    // 跳转商详
    const jumpProductDetail = (skuId: string, item: any, index: number) => {
      skuId && gotoDp(skuId);
      sendClickTrack.Shopcart_ProductCard_OtherPos_Click({ ...productCardTrack(item, skuId, index) });
    };

    const proTypeSelectFloor = (item: ProductItemInfo, itemParent: any, index: number) => {
      if (
        item[PRODUCT_ITEM_EXTINFO_KEY]?.propertyTagMap &&
        Object.values(item[PRODUCT_ITEM_EXTINFO_KEY].propertyTagMap).length > 0
      ) {
        const skuId = item?.[PRODUCT_ITEM_CORE_KEY]?.skuId;
        const uniqueId = `proTypeSelect-${skuId}`;
        return (
          <div>
            {item[PRODUCT_ITEM_EXTINFO_KEY].propertyTagMap && (
              <div
                className={styles["expanded"]}
                onClick={(event) => {
                  handleExpand(event);
                  setSkuId(skuId || "");
                  setCurProItemType(itemParent);
                  sendClickTrack.Shopcart_ProductCard_Spec_Click({ ...productCardTrack(itemParent, skuId, index) });
                }}
                data-id={uniqueId}
              >
                {Object.values(item[PRODUCT_ITEM_EXTINFO_KEY].propertyTagMap).join(", ")}
                <VectorDown
                  width={10}
                  className={
                    isExpanded && skuId === curSkuId
                      ? styles["svg-expanded"]
                      : styles["svg-expand"]
                  }
                />
              </div>
            )}
          </div>
        );
      }
      return null;
    };

    const markInfoDiscountFloor = (parsedMarkInfos: any) => {
      return (
        <div>
          {parsedMarkInfos.length > 0 &&
            parsedMarkInfos
              .filter((markInfo: any) => markInfo?.type === 2)
              .map((markInfo: any, index: number) => {
                return (
                  <div
                    key={index}
                    className={styles["tab-tag"]}
                    style={{
                      background: markInfo?.bgColor ? markInfo.bgColor : "",
                      marginTop: "0px",
                      marginLeft: "4px",
                      padding: "0px",
                    }}
                  >
                    {markInfo?.icon && (
                      <img style={{ width: "12px", height: "12px" }} src={markInfo?.icon} />
                    )}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: markInfo?.richText,
                      }}
                    ></span>
                  </div>
                );
              })}
        </div>
      );
    };

    const topAllContainer = () => {
      return (
        <div className={styles["top-select"]}>
          <div className={styles["all-select"]}>
            <Checkbox
              sx={{
                color: "#888B94",
                "&.Mui-checked": {
                  color: "#CC0C1C",
                },
                "&.MuiCheckbox-root:hover": {
                  backgroundColor: "#fff",
                },
              }}
              checked={
                (subtotal && subtotal.info?.["HJM-D#subtotal&other"]?.activeCheck === 1) || false
              }
              onClick={(event) => {
                const target = event.target as HTMLInputElement;
                handleCheckAll(target.checked);
                sendClickTrack.Shopcart_SelectAllBtn_Click({
                  selectStatus: target.checked ? "1" : "0",
                });
              }}
            />
            <span
              className={styles["info-left"]}
              onClick={() => {
                handleCheckAll(
                  !(subtotal && subtotal.info?.["HJM-D#subtotal&other"]?.activeCheck === 1),
                );
              }
              }
            >
              {t("cart.page.all")}
            </span>
          </div>
        </div>
      );
    };

    const hasStockContainer = (items: any, isHasStock = true) => {
      return (
        <List>
          {items &&
            items.length > 0 &&
            items.map((item: any, pos: number) => {
              const skuId = item?.info[PRODUCT_ITEM_CORE_KEY]?.skuId || "";
              const labelId = `checkbox-list-label-${skuId}`;
              const stockCode = item.info["HJM-M#cartProduct&stockInfo"] ? item.info["HJM-M#cartProduct&stockInfo"].stockCode : null;
              // 提取 productMarkInfo 的值
              const productMarkInfoValues = item?.info[PRODUCT_ITEM_EXTINFO_KEY]?.productMarkInfo;
              const productMarkSet = item?.info[PRODUCT_ITEM_EXTINFO_KEY]?.productMarkSet || [];
              // 根据 productMarkSet 的顺序获取对应的标记信息
              const sortedMarkInfos = productMarkSet?.map((markId: any) => {
                const markInfoJson = productMarkInfoValues && productMarkInfoValues[markId];
                return safeParse(markInfoJson);
              });
              // 315版本分堆了商品，如果无货需要加上有货的角标
              const index = isHasStock ? pos : pos + (productItems?.length || 0) + 1;
              return (
                <ListItem
                  key={skuId}
                  disablePadding
                >
                  <div className={styles["cart-item"]}>
                    <Checkbox
                      edge="start"
                      checked={item?.info[PRODUCT_ITEM_BASIC_KEY]?.activeCheck === 1}
                      tabIndex={-1}
                      disableRipple
                      sx={{
                        color: stockCode === 1 ? "#E0E1E5" : "#888B94",
                        height: "100px",
                        opacity: stockCode === 1 ? 0.4 : 1, // 无货时降低透明度置灰
                        "&.Mui-checked": {
                          color: "#CC0C1C",
                        },
                      }}
                      disabled={stockCode === 1} // 无货添加禁用属性
                      onClick={() => handleItemCheck(item)}
                    />
                    <div
                      className={styles["img-container"]}
                      onClick={() =>
                        jumpProductDetail(skuId, item, index)
                      }
                    >
                      <UKImg
                        externalStyles={{
                          opacity: stockCode === 1 ? 0.4 : 1,
                          width: "100px",
                          height: "100px",
                          objectFit: "contain",
                        }}
                        url={`${imageUtil.concatImgUrlWithoutForceHttps(item?.info[PRODUCT_ITEM_BASIC_KEY]?.imgUrl ? item.info[PRODUCT_ITEM_BASIC_KEY].imgUrl : "", 200, 200)}`}
                      />
                      {stockCode === 1 && <a className={styles["img-top"]}>{t("cart.page.stock")}</a>}
                    </div>
                    <div style={{ opacity: stockCode === 1 ? 0.4 : 1 }}>
                      {/* tag标签 商品名 */}
                      <div id={labelId} className={styles["text-container"]}>
                        {sortedMarkInfos.length > 0 &&
                          sortedMarkInfos
                            .filter((markInfo: any) => markInfo?.type === 0)
                            .map((markInfo: any, index: number) => (
                              <span className={styles["tag"]} key={index}>
                                {markInfo?.text}
                              </span>
                            ))}
                        <span
                          className={styles["name"]}
                          onClick={() =>
                            jumpProductDetail(skuId, item, index)
                          }
                        >
                          {item?.info[PRODUCT_ITEM_BASIC_KEY]?.skuName}
                        </span>
                      </div>
                      {/* 种类楼层 */}
                      {proTypeSelectFloor(item.info, item, index)}
                      {/* 标签楼层 */}
                      {markInfoFloor(sortedMarkInfos, item)}
                    </div>
                    {/* 价格楼层 */}
                    <div
                      className={styles["price-item"]}
                      style={{ opacity: stockCode === 1 ? 0.4 : 1 }}
                    >
                      <JDIPrice
                        symbol={cartData?.localInfo?.currencySymbol || "£"}
                        price={item?.info[PRODUCT_ITEM_EXTINFO_KEY]?.landedPrice || ""}
                        uiType={1}
                        containerStyle={{
                          fontSize: "16px",
                          lineHeight: "20px",
                        }}
                        integerStyle={{
                          fontSize: "24px",
                          lineHeight: "24px",
                          height: "24px",
                        }}
                      />
                      {item?.info[PRODUCT_ITEM_EXTINFO_KEY]?.landedPrice !==
                        item?.info[PRODUCT_ITEM_BASIC_KEY]?.price && (
                          <div style={{ display: "flex" }}>
                            <JDIPrice
                              symbol={cartData?.localInfo?.currencySymbol || "£"}
                              price={item?.info[PRODUCT_ITEM_BASIC_KEY]?.price || ""}
                              uiType={2}
                              containerStyle={{
                                fontSize: "16px",
                                lineHeight: "16px",
                                color: "#888B94",
                              }}
                              lineThrough
                            />
                            {/* 折扣标签楼层 */}
                            {markInfoDiscountFloor(sortedMarkInfos)}
                          </div>
                        )}
                    </div>
                    {/* 加减商品组件楼层 */}
                    {stockCode === 1 ? (
                      <span
                        className={styles["counter-delete"]}
                        onClick={() => {
                          handleDeleteSingle(item);
                        }}
                      >
                        <ItemDelete />
                      </span>
                    ) : (<div
                      style={{
                        opacity: stockCode === 1 ? 0.5 : 1,
                        pointerEvents: stockCode === 1 ? "none" : "auto", // 无货时禁止鼠标事件
                      }}
                    >
                      <UKCounter
                        immediately={false}
                        value={item?.info[PRODUCT_ITEM_BASIC_KEY]?.num ? Number(item.info[PRODUCT_ITEM_BASIC_KEY].num) : 1}
                        minAmount={0}
                        maxAmount={item?.info[PRODUCT_ITEM_EXTINFO_KEY]?.maxBuyNum ? Number(item.info[PRODUCT_ITEM_EXTINFO_KEY].maxBuyNum) : 200}
                        isShowDeleteIcon={true}
                        size="middle"
                        // isSetDefaultValue={
                        //   cancelDeleteItemId === skuId
                        // }
                        // setIsCancelDelete={() => {
                        //   if (cancelDeleteItemId === skuId) {
                        //     setCancelDeleteItemId("");
                        //   }
                        // }}
                        onChange={(num: any) => {
                          console.log("num 数量", num);
                          const maxAmount = item?.info[PRODUCT_ITEM_EXTINFO_KEY]?.maxBuyNum
                            ? Number(item.info[PRODUCT_ITEM_EXTINFO_KEY].maxBuyNum)
                            : 200;
                          if (num > maxAmount) {
                            setTimeout(() => { // 加入事件循环队列
                              UKToast.show({
                                content: `${t("cart.maxSelectTips")}`,
                                icon: "warning",
                                duration: 1000,
                              });
                            }, 200);
                            eventTrackingUtils.submitExp({
                              event_id: EXPOSURE_RECORD.Shopcart_ToastPromptStrip_Expo,
                              json_param: { displayText: "The selected quantity exceeds the maximum available." },
                            });
                            return;
                          }
                          if (num === 0) {
                            handleDeleteSingle(item);
                            // setAgainDelete(true);
                            // setIsCancelDelete(true);
                            return;
                          }
                          handleChangeNum(item, num);
                        }}
                        onCounterType={(type: string) => {
                          sendClickTrack.Shopcart_ProductNumEditBox_Click(counterTrack(skuId, type));
                        }}
                      />
                    </div>)}
                  </div>
                </ListItem>
              );
            })}
        </List>
      );
    };

    const removeContainer = (items: any) => {
      return (
        <List>
          {items &&
            items.length > 0 &&
            items.map((item: any, pos: number) => {
              const skuId = item?.info?.["C-M#abstractUnusableProductItem&core"]?.skuId || "";
              const labelId = `checkbox-list-label-${skuId}`;
              // 315商品采用了分堆，这里需要加上有货和无货商品的位置
              const index = pos + 1 + (productItems?.length || 0) + (productItemsNoStock?.length || 0);
              return (
                <ListItem
                  key={skuId}
                  disablePadding
                >
                  <div className={styles["cart-item"]}>
                    <Checkbox
                      edge="start"
                      checked={false}
                      tabIndex={-1}
                      disableRipple
                      sx={{
                        color: "#E0E1E5",
                        height: "100px",
                        opacity: 0.4,
                        "&.Mui-checked": {
                          color: "#CC0C1C",
                        },
                      }}
                      disabled={true} // 添加禁用属性
                    />
                    <div
                      className={styles["img-container"]}
                    >
                      <UKImg
                        externalStyles={{
                          opacity: 0.4,
                          width: "100px",
                          height: "100px",
                          objectFit: "contain",
                        }}
                        url={`${imageUtil.concatImgUrlWithoutForceHttps(item?.info["C-M#abstractUnusableProductItem&basic"]?.imgUrl ? item.info["C-M#abstractUnusableProductItem&basic"].imgUrl : "", 200, 200)}`}
                      />
                      <a className={styles["img-top"]}>{t("cart.page.available")}</a>
                    </div>
                    <div style={{ opacity: 0.4 }}>
                      {/* tag标签 商品名 */}
                      <div id={labelId} className={styles["text-container"]} style={{ cursor: "auto" }}>
                        <span
                          className={styles["remove-name"]}
                        >
                          {item?.info["C-M#abstractUnusableProductItem&basic"]?.skuName}
                        </span>
                      </div>
                    </div>
                    {/* 价格楼层 空占位*/}
                    <div className={styles["price-item"]} style={{ opacity: 0.4 }}></div>
                    <span
                      className={styles["counter-delete"]}
                      onClick={() => {
                        handleDeleteSingle(item);
                      }}
                    >
                      <ItemDelete />
                    </span>
                  </div>
                </ListItem>
              );
            })}
        </List>
      );
    };

    return (
      <div className={styles["container"]}>
        {topAllContainer()}

        {/* 有货 */}
        {productItems && productItems.length > 0 && (
          <div className={styles["card-stock"]}>
            {hasStockContainer(productItems)}
          </div>
        )}

        {/* 无货 */}
        {productItemsNoStock && productItemsNoStock.length > 0 && (
          <div className={styles["card"]}>
            {hasStockContainer(productItemsNoStock, false)}
          </div>
        )}

        {/* 下架 */}
        {productItemsRemove && productItemsRemove.length > 0 && (
          <div className={styles["card"]}>
            <div className={styles["unavailable"]}>
              <span className={styles["info-left"]}>{t("cart.page.unavailableItems")} ({productItemsRemove.length})</span>
              <span
                className={styles["info-right"]}
                onClick={() => {
                  handleDeleteAll();
                }}
              >
                {t("cart.page.deleteAll")}
              </span>
            </div>
            {removeContainer(productItemsRemove)}
          </div>
        )}

        {/* 选择商品类型弹窗 */}
        <Popover
          id={id}
          open={isOpenTypeSelect}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          sx={{ marginTop: "8px" }}
        >
          <Typography
            sx={{ p: 2 }}
            style={{
              width: "632px",
              maxHeight: "446px",
            }}
          >
            <ProTypeModal
              skuId={curSkuId}
              isOpenTypeSelect={isOpenTypeSelect}
              handleClose={handleClose}
              updateItemData={updateItemData}
            />
          </Typography>
        </Popover>
      </div>
    );
  },
);

export default CartList;
